// components/TopInvoices100Table.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import {
  FiAward, FiStar, FiTrendingUp, FiUsers, FiDollarSign,
  FiShoppingBag, FiRefreshCw, FiDownload, FiSearch,
  FiPhone, FiChevronLeft, FiChevronRight, FiChevronsLeft,
  FiChevronsRight, FiBarChart2, FiTarget, FiWifi, FiWifiOff,
  FiDatabase, FiClock, FiUser, FiCalendar, FiArrowUp, FiArrowDown,
  FiPercent, FiActivity, FiEye, FiX,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// -------------------- UTILITAIRES GLOBAUX --------------------
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 FC';
  return new Intl.NumberFormat('fr-FR').format(amount) + ' FC';
};

const formatNumber = (num) => new Intl.NumberFormat('fr-FR').format(num);

const formatDateCache = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Date invalide';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
};

// -------------------- BASE DE DONNÉES & UTILITAIRES --------------------
const db = new Dexie('TopClientsAnalyticsCache');
db.version(1).stores({
  clients: 'id, nom, prenom, tel, totalFact, TTC',
  cacheInfo: 'key',
});

class RateLimiter {
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;
    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);
      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }
    this.isProcessing = false;
  }
}

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const rateLimiter = new RateLimiter(3, 60000);

const isCacheValid = async () => {
  try {
    const cacheInfo = await db.cacheInfo.get('top_clients');
    if (!cacheInfo) return false;
    const now = new Date();
    const cacheDate = new Date(cacheInfo.timestamp);
    const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  } catch (error) {
    console.error('Erreur cache:', error);
    return false;
  }
};

const saveToCache = async (clients) => {
  try {
    await db.clients.clear();
    const clientsWithId = clients.map((client, index) => ({ ...client, cacheId: index + 1 }));
    if (clientsWithId.length > 0) await db.clients.bulkAdd(clientsWithId);
    await db.cacheInfo.put({ key: 'top_clients', timestamp: new Date().toISOString() });
  } catch (error) {
    console.error('Erreur sauvegarde cache:', error);
  }
};

const loadFromCache = async () => {
  try {
    const clients = await db.clients.toArray();
    return clients.map(({ cacheId, ...rest }) => rest);
  } catch (error) {
    console.error('Erreur chargement cache:', error);
    return [];
  }
};

const clearCache = async () => {
  try {
    await db.clients.clear();
    await db.cacheInfo.clear();
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

// -------------------- COMPOSANTS RÉUTILISABLES --------------------
const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

const ClientCardMobile = ({ client, rank, onClick }) => {
  const [expanded, setExpanded] = useState(false);
  const rankStyle = rank === 1
    ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black'
    : rank === 2
    ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black'
    : rank === 3
    ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white'
    : 'bg-blue-500/20 text-blue-400';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 mb-3"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${rankStyle}`}>
          {rank <= 3 ? <FiAward size={18} /> : rank}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm truncate">{client.nom} {client.prenom}</h3>
          <p className="text-slate-400 text-xs">{client.tel || '—'}</p>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-1 text-slate-400 hover:text-white">
          {expanded ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-slate-400">Factures</div>
          <div className="text-blue-400 font-bold">{formatNumber(client.totalFact)}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-slate-400">CA TTC</div>
          <div className="text-emerald-400 font-bold">{formatCurrency(client.TTC)}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-slate-400">HT</div>
          <div className="text-white">{formatCurrency(client.TH)}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-slate-400">Moy.</div>
          <div className="text-amber-400">{formatCurrency(client.panier_moyen)}</div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-3 border-t border-white/10 space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">TVA</span><span className="text-amber-300">{formatCurrency(client.TAX)}</span></div>
              {client.last_purchase && (
                <div className="flex justify-between"><span className="text-slate-400">Dernier achat</span><span>{new Date(client.last_purchase).toLocaleDateString('fr-FR')}</span></div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => onClick(client)}
        className="w-full mt-3 py-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 text-xs font-medium flex items-center justify-center gap-1 hover:bg-blue-500/20 transition-colors"
      >
        <FiEye size={14} /> Détails
      </button>
    </motion.div>
  );
};

// -------------------- COMPOSANT PRINCIPAL --------------------
const TopInvoices100Table = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState('totalFact');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);
  const [minFactures, setMinFactures] = useState(1);
  const [stats, setStats] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  const fetchTopClients = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && lastFetchRef.current && Date.now() - lastFetchRef.current < 10000) {
        const cached = await loadFromCache();
        if (cached.length) {
          setData(cached);
          return;
        }
      }
      setLoading(true);
      setError(null);
      if (!forceRefresh) {
        const cacheValid = await isCacheValid();
        if (cacheValid) {
          const cached = await loadFromCache();
          if (cached.length) {
            setData(cached);
            const cacheInfo = await db.cacheInfo.get('top_clients');
            if (cacheInfo) setCacheDate(cacheInfo.timestamp);
            setSuccessMessage(`📦 ${cached.length} clients chargés depuis le cache`);
            setTimeout(() => setSuccessMessage(null), 3000);
            if (isOnline) debouncedFetch(() => refreshDataInBackground(), 15000);
            setLoading(false);
            return;
          }
        }
      }
      await rateLimiter.acquire();
      const url = `${API_CONFIG.apiUrl}TopListe.php?page=1&limit=100&min_factures=${minFactures}&sort_by=totalFact&sort_order=desc`;
      const response = await fetchWithRetry(url);
      const result = await response.json();
      if (result.success) {
        const sorted = (result.data || []).sort((a, b) => (b.totalFact || 0) - (a.totalFact || 0));
        setData(sorted);
        setStats(result.stats);
        await saveToCache(sorted);
        lastFetchRef.current = Date.now();
        const cacheInfo = await db.cacheInfo.get('top_clients');
        if (cacheInfo) setCacheDate(cacheInfo.timestamp);
        setSuccessMessage(`✅ ${sorted.length} clients chargés`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      if (err.message?.includes('429') || err.status === 429) {
        setError('Trop de requêtes. Patientez...');
        setTimeout(() => fetchTopClients(false), 30000);
      } else {
        const cached = await loadFromCache();
        if (cached.length) {
          setData(cached);
          setError(null);
          setSuccessMessage(`📦 ${cached.length} clients chargés depuis le cache`);
          setTimeout(() => setSuccessMessage(null), 4000);
        } else {
          setError('Impossible de charger les données.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, minFactures, debouncedFetch]);

  const refreshDataInBackground = async () => {
    try {
      await rateLimiter.acquire();
      const url = `${API_CONFIG.apiUrl}TopListe.php?limit=100&min_factures=${minFactures}&sort_by=totalFact&sort_order=desc`;
      const response = await fetchWithRetry(url);
      const result = await response.json();
      if (result.success && result.data) {
        const sorted = result.data.sort((a, b) => (b.totalFact || 0) - (a.totalFact || 0));
        setData(sorted);
        setStats(result.stats);
        await saveToCache(sorted);
        lastFetchRef.current = Date.now();
      }
    } catch (err) { /* silent */ }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setIsRefreshing(true);
    try { await fetchTopClients(true); } catch { setError('Échec du rafraîchissement'); }
    finally { setIsRefreshing(false); }
  };

  const clearCacheAndReload = async () => {
    await clearCache();
    lastFetchRef.current = null;
    await fetchTopClients(true);
  };

  useEffect(() => {
    fetchTopClients();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, [fetchTopClients]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const s = searchTerm.toLowerCase();
    return data.filter(client =>
      client.nom?.toLowerCase().includes(s) ||
      client.prenom?.toLowerCase().includes(s) ||
      client.tel?.toLowerCase().includes(s)
    );
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    if (sortBy === 'totalFact') {
      sorted.sort((a, b) => sortOrder === 'desc' ? (b.totalFact || 0) - (a.totalFact || 0) : (a.totalFact || 0) - (b.totalFact || 0));
    } else if (sortBy === 'TTC') {
      sorted.sort((a, b) => sortOrder === 'desc' ? (b.TTC || 0) - (a.TTC || 0) : (a.TTC || 0) - (b.TTC || 0));
    } else if (sortBy === 'TH') {
      sorted.sort((a, b) => sortOrder === 'desc' ? (b.TH || 0) - (a.TH || 0) : (a.TH || 0) - (b.TH || 0));
    } else if (sortBy === 'nom') {
      sorted.sort((a, b) => {
        const nameA = `${a.nom || ''} ${a.prenom || ''}`.toLowerCase();
        const nameB = `${b.nom || ''} ${b.prenom || ''}`.toLowerCase();
        return sortOrder === 'desc' ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
      });
    }
    return sorted;
  }, [filteredData, sortBy, sortOrder]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortOrder('desc'); }
    setCurrentPage(1);
  };

  const extendedStats = useMemo(() => {
    if (!data.length) return null;
    const ttcs = data.map(c => c.TTC || 0).sort((a, b) => a - b);
    const total = ttcs.reduce((s, v) => s + v, 0);
    const mean = total / ttcs.length;
    const median = ttcs.length % 2 === 0 ? (ttcs[ttcs.length / 2 - 1] + ttcs[ttcs.length / 2]) / 2 : ttcs[Math.floor(ttcs.length / 2)];
    const variance = ttcs.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / ttcs.length;
    const stdDev = Math.sqrt(variance);
    const q1 = ttcs[Math.floor(ttcs.length * 0.25)];
    const q3 = ttcs[Math.floor(ttcs.length * 0.75)];
    return { total, mean, median, stdDev, q1, q3, count: ttcs.length };
  }, [data]);

  const handleExport = () => {
    if (!sortedData.length) return;
    const headers = ['Rang', 'ID', 'Nom', 'Prénom', 'Téléphone', 'Total Factures', 'Total HT', 'TVA', 'Total TTC', 'Panier Moyen'];
    const csvData = sortedData.map((client, index) => [
      index + 1, client.id, client.nom, client.prenom, client.tel,
      client.totalFact || 0, client.TH || 0, client.TAX || 0,
      client.TTC || 0, client.panier_moyen || 0,
    ]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `top_clients_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setSuccessMessage('Export CSV effectué');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (loading && !data.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-slate-300">Chargement des clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        {/* Cache info bar */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-3 md:p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs md:text-sm text-slate-300">
          <div className="flex items-center gap-2 flex-wrap">
            <FiDatabase className="text-blue-400" />
            <span>{cacheDate ? `📦 Cache du ${formatDateCache(cacheDate)}` : '🔄 Données fraîches'}</span>
            <FiClock className="text-amber-400 ml-2" />
            <span>Cache 24h</span>
          </div>
          <button onClick={clearCacheAndReload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-xs">Vider cache</button>
        </div>

        {/* Desktop header */}
        <div className="hidden md:block mb-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent flex items-center gap-2">
                  <FiAward className="text-amber-400" /> Top Clients <FiTrendingUp className="text-emerald-400" />
                </h1>
                <p className="text-slate-400 text-sm mt-1">Classement par nombre de factures</p>
              </div>
              <div className="flex gap-3">
                <button onClick={manualRefresh} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-sm disabled:opacity-50">
                  <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
                <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm">
                  <FiDownload /> Exporter
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mobile header */}
        <div className="md:hidden mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Top Clients</h1>
            <p className="text-slate-400 text-xs">{data.length} clients</p>
          </div>
          <div className="flex gap-2">
            <button onClick={manualRefresh} disabled={isRefreshing} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} size={18} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          {[
            { label: 'Clients', value: data.length, icon: FiUsers, color: 'text-blue-400' },
            { label: 'Factures totales', value: stats?.total_factures ? formatNumber(stats.total_factures) : '—', icon: FiShoppingBag, color: 'text-purple-400' },
            { label: 'CA Total', value: stats?.total_ttc ? formatCurrency(stats.total_ttc) : '—', icon: FiDollarSign, color: 'text-emerald-400' },
            { label: 'Panier moyen', value: stats?.average_basket ? formatCurrency(stats.average_basket) : '—', icon: FiBarChart2, color: 'text-amber-400' },
          ].map((s, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-400/50 to-purple-400/50" />
              <s.icon className={`text-2xl mb-2 ${s.color}`} />
              <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
              <div className="text-slate-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {extendedStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
            {[
              { label: 'Médiane CA', value: formatCurrency(extendedStats.median), icon: FiPercent, color: 'text-cyan-400' },
              { label: 'Écart-type', value: formatCurrency(extendedStats.stdDev), icon: FiActivity, color: 'text-pink-400' },
              { label: 'Q1 - Q3', value: `${formatCurrency(extendedStats.q1)} - ${formatCurrency(extendedStats.q3)}`, icon: FiTarget, color: 'text-indigo-400' },
              { label: 'Moyenne', value: formatCurrency(extendedStats.mean), icon: FiTrendingUp, color: 'text-blue-400' },
            ].map((s, i) => (
              <div key={i} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-400/50 to-purple-400/50" />
                <s.icon className={`text-2xl mb-2 ${s.color}`} />
                <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
                <div className="text-slate-400 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filtres & recherche */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, téléphone..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={minFactures} onChange={(e) => { setMinFactures(Number(e.target.value)); setCurrentPage(1); }} className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white text-sm">
              <option value={1}>1+</option><option value={10}>10+</option><option value={20}>20+</option><option value={50}>50+</option><option value={100}>100+</option>
            </select>
            <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/10">
              {['totalFact', 'TTC', 'nom'].map(field => (
                <button key={field} onClick={() => handleSort(field)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${sortBy === field ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white'}`}>
                  {field === 'totalFact' ? 'Factures' : field === 'TTC' ? 'CA TTC' : 'Nom'}
                  {sortBy === field && (sortOrder === 'asc' ? <FiArrowUp size={12} className="inline ml-1" /> : <FiArrowDown size={12} className="inline ml-1" />)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tableau desktop */}
        <div className="hidden md:block">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-slate-400">
                    <th className="p-4">Rang</th><th className="p-4">ID</th><th className="p-4">Client</th><th className="p-4">Téléphone</th>
                    <th className="p-4 text-right cursor-pointer" onClick={() => handleSort('totalFact')}>Factures {sortBy === 'totalFact' && (sortOrder === 'asc' ? <FiArrowUp size={14} className="inline" /> : <FiArrowDown size={14} className="inline" />)}</th>
                    <th className="p-4 text-right">HT</th><th className="p-4 text-right">TVA</th>
                    <th className="p-4 text-right cursor-pointer" onClick={() => handleSort('TTC')}>TTC {sortBy === 'TTC' && (sortOrder === 'asc' ? <FiArrowUp size={14} className="inline" /> : <FiArrowDown size={14} className="inline" />)}</th>
                    <th className="p-4 text-right">Moy.</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-500">Aucun client trouvé</td></tr>
                  ) : (
                    paginatedData.map((client, idx) => {
                      const rank = (currentPage - 1) * itemsPerPage + idx + 1;
                      const rankStyle = rank === 1 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black' : rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black' : rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' : 'bg-blue-500/20 text-blue-400';
                      return (
                        <tr key={client.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => { setSelectedClient(client); setShowModal(true); }}>
                          <td className="p-4"><span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${rankStyle}`}>{rank <= 3 ? <FiAward size={14} /> : rank}</span></td>
                          <td className="p-4 font-mono text-blue-400">#{client.id}</td>
                          <td className="p-4 font-medium text-white">{client.nom} {client.prenom}</td>
                          <td className="p-4 text-slate-300">{client.tel || '—'}</td>
                          <td className="p-4 text-right text-blue-400 font-bold">{formatNumber(client.totalFact)}</td>
                          <td className="p-4 text-right text-slate-300">{formatCurrency(client.TH)}</td>
                          <td className="p-4 text-right text-amber-400">{formatCurrency(client.TAX)}</td>
                          <td className="p-4 text-right text-emerald-400 font-bold">{formatCurrency(client.TTC)}</td>
                          <td className="p-4 text-right text-blue-300">{formatCurrency(client.panier_moyen)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-white/10">
                <div className="text-sm text-slate-400">{((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, sortedData.length)} sur {sortedData.length}</div>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsLeft size={16} /></button>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronLeft size={16} /></button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium ${currentPage === pageNum ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' : 'text-slate-400 hover:bg-white/10'}`}>{pageNum}</button>;
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronRight size={16} /></button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsRight size={16} /></button>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <span>Afficher</span>
                  <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white">
                    <option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                  </select>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Cartes mobiles */}
        <div className="md:hidden space-y-3">
          {paginatedData.length === 0 ? (
            <div className="text-center py-10 text-slate-400">Aucun client</div>
          ) : (
            paginatedData.map((client, idx) => (
              <ClientCardMobile key={client.id} client={client} rank={(currentPage - 1) * itemsPerPage + idx + 1} onClick={(c) => { setSelectedClient(c); setShowModal(true); }} />
            ))
          )}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 py-4">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronLeft size={16} /></button>
              <span className="text-slate-400 text-sm">Page {currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronRight size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {/* Modal détails */}
      <AnimatePresence>
        {showModal && selectedClient && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><FiUser className="text-blue-400" /> {selectedClient.nom} {selectedClient.prenom}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={20} /></button>
              </div>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500">ID</span><p className="text-white font-mono">#{selectedClient.id}</p></div>
                  <div><span className="text-slate-500">Téléphone</span><p className="text-blue-300">{selectedClient.tel || '—'}</p></div>
                </div>
                <div>
                  <h3 className="text-blue-400 font-semibold mb-2">Statistiques d'achat</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-xl p-3 text-center"><div className="text-xs text-slate-400">Factures</div><div className="text-white font-bold">{formatNumber(selectedClient.totalFact)}</div></div>
                    <div className="bg-white/5 rounded-xl p-3 text-center"><div className="text-xs text-slate-400">Panier moyen</div><div className="text-white font-bold">{formatCurrency(selectedClient.panier_moyen)}</div></div>
                    <div className="bg-white/5 rounded-xl p-3 text-center"><div className="text-xs text-slate-400">Total HT</div><div className="text-white font-bold">{formatCurrency(selectedClient.TH)}</div></div>
                    <div className="bg-white/5 rounded-xl p-3 text-center"><div className="text-xs text-slate-400">TVA</div><div className="text-amber-400 font-bold">{formatCurrency(selectedClient.TAX)}</div></div>
                    <div className="col-span-2 bg-white/5 rounded-xl p-3 text-center"><div className="text-xs text-slate-400">Total TTC</div><div className="text-emerald-400 font-bold text-lg">{formatCurrency(selectedClient.TTC)}</div></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notifications */}
      <AnimatePresence>
        {successMessage && <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-4 right-4 z-50 px-5 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-2xl backdrop-blur-xl text-sm">{successMessage}</motion.div>}
        {error && <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-4 right-4 z-50 px-5 py-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl backdrop-blur-xl text-sm">{error}</motion.div>}
      </AnimatePresence>

      <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xl border ${isOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </div>
    </div>
  );
};

export default TopInvoices100Table;